/**
 * Coffee Bean Clicker v2 - Game State
 * Pure data + pure calculation functions
 * No window, document, PIXI
 */

import {
  TAP_UPGRADES, PRODUCERS, PRESTIGE_UPGRADES, STAR_UPGRADES, VARIETIES, STAGES,
  MILESTONE_THRESHOLDS, MILESTONE_MULTIPLIERS, COMBO_TIERS,
  type TapUpgradeDef, type ProducerDef, type ComboTierDef,
} from './data/upgrades.js';
import { ACHIEVEMENTS, type AchievementReward } from './data/achievements.js';

// ═══════════════════════════════════════════════
//  State Interface
// ═══════════════════════════════════════════════

export interface GameState {
  // ── Currencies ──
  beans: number;
  goldenBeans: number;
  starDust: number;

  // ── Upgrades ──
  tapUpgrades: Record<string, number>;
  producers: Record<string, number>;
  prestigeUpgrades: Record<string, number>;
  starUpgrades: Record<string, number>;

  // ── Achievements / Varieties ──
  unlockedAchievements: string[];
  unlockedVarieties: string[];

  // ── Combo ──
  combo: {
    count: number;
    timer: number;       // seconds remaining until combo resets
    maxTimer: number;     // base 1.5s, can be increased by S5
  };

  // ── Boosts ──
  boosts: {
    bpsMultiplier: number;
    bpsEndTime: number;
    tapMultiplier: number;
    tapEndTime: number;
  };

  // ── Golden Cherry (roasting minigame trigger) ──
  goldenCherry: {
    active: boolean;
    nextSpawnTime: number;
    endTime: number;
  };

  // ── Roasting Minigame ──
  roasting: {
    active: boolean;
    heatLevel: number;    // 0.0 ~ 1.0
    speed: number;        // heat per second
    result: number | null; // multiplier result (null = not yet finished)
  };

  // ── Stage ──
  currentStage: number;

  // ── Stats ──
  stats: GameStats;

  // ── Meta ──
  isRunning: boolean;
  playTime: number;
  lastSaveTimestamp: number;
  saveVersion: number;

  // ── Settings ──
  settings: {
    bgmEnabled: boolean;
    sfxEnabled: boolean;
    vibrationEnabled: boolean;
    language: string;
  };
}

export interface GameStats {
  totalBeans: number;
  totalBeansThisRun: number;
  totalTaps: number;
  totalUpgradesBought: number;
  totalPrestiges: number;
  totalAscensions: number;
  totalGoldenBeansEarned: number;
  totalAdsWatched: number;
  totalCritTaps: number;
  goldenCherryTaps: number;
  perfectRoasts: number;
  maxTapsPerSecond: number;
  maxCombo: number;
  maxOfflineSeconds: number;
  maxPrestigeGB: number;
  fastestPrestige: number;     // seconds (0 = never prestiged)
  peakBPS: number;
  consecutiveLoginDays: number;
  lastLoginDate: string;
  prestigeStartTime: number;
}

// ═══════════════════════════════════════════════
//  Initial State
// ═══════════════════════════════════════════════

export function createInitialState(): GameState {
  const tapUpgrades: Record<string, number> = {};
  for (const t of TAP_UPGRADES) tapUpgrades[t.id] = 0;

  const producers: Record<string, number> = {};
  for (const p of PRODUCERS) producers[p.id] = 0;

  const prestigeUpgrades: Record<string, number> = {};
  for (const p of PRESTIGE_UPGRADES) prestigeUpgrades[p.id] = 0;

  const starUpgrades: Record<string, number> = {};
  for (const s of STAR_UPGRADES) starUpgrades[s.id] = 0;

  return {
    beans: 0,
    goldenBeans: 0,
    starDust: 0,
    tapUpgrades,
    producers,
    prestigeUpgrades,
    starUpgrades,
    unlockedAchievements: [],
    unlockedVarieties: [],
    combo: { count: 0, timer: 0, maxTimer: 1.5 },
    boosts: { bpsMultiplier: 1, bpsEndTime: 0, tapMultiplier: 1, tapEndTime: 0 },
    goldenCherry: { active: false, nextSpawnTime: 60, endTime: 0 },
    roasting: { active: false, heatLevel: 0, speed: 0.4, result: null },
    currentStage: 1,
    stats: {
      totalBeans: 0,
      totalBeansThisRun: 0,
      totalTaps: 0,
      totalUpgradesBought: 0,
      totalPrestiges: 0,
      totalAscensions: 0,
      totalGoldenBeansEarned: 0,
      totalAdsWatched: 0,
      totalCritTaps: 0,
      goldenCherryTaps: 0,
      perfectRoasts: 0,
      maxTapsPerSecond: 0,
      maxCombo: 0,
      maxOfflineSeconds: 0,
      maxPrestigeGB: 0,
      fastestPrestige: 0,
      peakBPS: 0,
      consecutiveLoginDays: 0,
      lastLoginDate: '',
      prestigeStartTime: 0,
    },
    isRunning: true,
    playTime: 0,
    lastSaveTimestamp: Date.now(),
    saveVersion: 2,
    settings: {
      bgmEnabled: true,
      sfxEnabled: true,
      vibrationEnabled: true,
      language: 'ko',
    },
  };
}

// ═══════════════════════════════════════════════
//  Pure Calculation Functions
// ═══════════════════════════════════════════════

/** Tap upgrade cost */
export function getTapUpgradeCost(def: TapUpgradeDef, currentLevel: number): number {
  let cost = Math.floor(def.baseCost * Math.pow(def.costMul, currentLevel));
  return cost;
}

/** Producer cost */
export function getProducerCost(def: ProducerDef, owned: number): number {
  return Math.floor(def.baseCost * Math.pow(def.costMul, owned));
}

/** Apply cost discount from prestige/star upgrades */
export function applyCostDiscount(state: GameState, cost: number, producerId?: string): number {
  let c = cost;
  // G3: P1~P3 -50%
  if (state.prestigeUpgrades['G3'] && producerId && ['P1', 'P2', 'P3'].includes(producerId)) {
    c = Math.floor(c * 0.5);
  }
  // G8: all -20%
  if (state.prestigeUpgrades['G8']) c = Math.floor(c * 0.8);
  return c;
}

/** Compute tap power */
export function computeTapPower(state: GameState): number {
  let base = 1;
  for (const def of TAP_UPGRADES) {
    base += def.tapPower * (state.tapUpgrades[def.id] ?? 0);
  }

  let mul = 1;
  // Prestige upgrades
  if (state.prestigeUpgrades['G2']) mul *= 1.5;
  if (state.prestigeUpgrades['G7']) mul *= 3;
  if (state.prestigeUpgrades['G12']) mul *= 2;
  if (state.prestigeUpgrades['G15']) mul *= 10;

  // Star upgrades
  if (state.starUpgrades['S3']) mul *= 5;
  if (state.starUpgrades['S7']) mul *= 10;

  // Golden bean bonus (+2% per bean)
  mul *= 1 + (state.goldenBeans * 0.02);

  // Achievement bonus
  mul *= 1 + getAchievementBonusSum(state, 'tapPct') / 100;

  // Variety bonus
  mul *= 1 + getVarietyBonusSum(state, 'tap') / 100;

  // Boost
  if (state.playTime < state.boosts.tapEndTime) {
    mul *= state.boosts.tapMultiplier;
  }

  // Final multiplier
  mul *= getFinalMultiplier(state);

  return Math.floor(base * mul);
}

/** Compute BPS */
export function computeBPS(state: GameState): number {
  let totalBPS = 0;
  for (const def of PRODUCERS) {
    const count = state.producers[def.id] ?? 0;
    if (count <= 0) continue;

    let bps = def.baseBPS * count;

    // Milestones
    for (let i = 0; i < MILESTONE_THRESHOLDS.length; i++) {
      if (count >= MILESTONE_THRESHOLDS[i]) bps *= MILESTONE_MULTIPLIERS[i];
    }

    totalBPS += bps;
  }

  let mul = 1;
  // Prestige
  if (state.prestigeUpgrades['G1']) totalBPS += 1;
  if (state.prestigeUpgrades['G5']) mul *= 2;
  if (state.prestigeUpgrades['G10']) mul *= 3;
  if (state.prestigeUpgrades['G12']) mul *= 2;
  if (state.prestigeUpgrades['G15']) mul *= 10;

  // Star
  if (state.starUpgrades['S3']) mul *= 5;
  if (state.starUpgrades['S7']) mul *= 10;

  // Golden bean bonus
  mul *= 1 + (state.goldenBeans * 0.02);

  // Achievement bonuses
  mul *= 1 + getAchievementBonusSum(state, 'bpsPct') / 100;
  mul *= 1 + getAchievementBonusSum(state, 'productionPct') / 100;

  // Variety bonuses
  mul *= 1 + getVarietyBonusSum(state, 'bps') / 100;
  mul *= 1 + getVarietyBonusSum(state, 'production') / 100;

  // Boost
  if (state.playTime < state.boosts.bpsEndTime) {
    mul *= state.boosts.bpsMultiplier;
  }

  // Speed bonus (from prestige speed achievements)
  mul *= 1 + getAchievementBonusSum(state, 'speedPct') / 100;

  // Final multiplier
  mul *= getFinalMultiplier(state);

  return totalBPS * mul;
}

/** Final multiplier from achievements + varieties */
function getFinalMultiplier(state: GameState): number {
  const achFinal = getAchievementBonusSum(state, 'finalMul');
  const varFinal = getVarietyBonusSum(state, 'finalMul');
  return 1 + (achFinal + varFinal) / 100;
}

/** Combo multiplier */
export function getComboMultiplier(comboCount: number): number {
  for (const tier of COMBO_TIERS) {
    if (comboCount >= tier.minCombo) return tier.multiplier;
  }
  return 1;
}

/** Combo tier label */
export function getComboTier(comboCount: number): ComboTierDef | null {
  for (const tier of COMBO_TIERS) {
    if (comboCount >= tier.minCombo) return tier;
  }
  return null;
}

/** Critical chance (%) */
export function getCritChance(state: GameState): number {
  let chance = 5;
  if (state.prestigeUpgrades['G6']) chance += 3;
  chance += getAchievementBonusSum(state, 'critChance');
  chance += getVarietyBonusSum(state, 'crit');
  return Math.min(chance, 30);
}

/** Critical multiplier */
export function getCritMultiplier(state: GameState): number {
  let mul = 5;
  if (state.starUpgrades['S6']) mul *= 2;
  const bonusPct = getAchievementBonusSum(state, 'critMul');
  mul *= 1 + bonusPct / 100;
  return Math.min(mul, 50);
}

/** Prestige 1: golden beans earned */
export function computePrestigeGoldenBeans(state: GameState): number {
  const total = state.stats.totalBeansThisRun;
  if (total < 1_000_000_000_000) return 0;

  let gb = Math.floor(150 * Math.sqrt(total / 1e15));
  if (state.prestigeUpgrades['G13']) gb = Math.floor(gb * 1.2);
  if (state.prestigeUpgrades['G16']) gb *= 2;
  if (state.starUpgrades['S2']) gb *= 2;

  gb = Math.floor(gb * (1 + getAchievementBonusSum(state, 'goldenBeanPct') / 100));
  gb = Math.floor(gb * (1 + getVarietyBonusSum(state, 'goldenBean') / 100));

  return Math.max(0, gb);
}

/** Can prestige (stage 1) */
export function canPrestige(state: GameState): boolean {
  return state.currentStage >= 7;
}

/** Prestige 2: star dust earned */
export function computeAscensionStarDust(state: GameState): number {
  const totalGB = state.stats.totalGoldenBeansEarned;
  if (totalGB < 100) return 0;
  let sd = Math.floor(10 * Math.sqrt(totalGB / 100));
  if (state.starUpgrades['S8']) sd *= 3;
  sd = Math.floor(sd * (1 + getAchievementBonusSum(state, 'starDustPct') / 100));
  return Math.max(0, sd);
}

/** Can ascend (stage 2) */
export function canAscend(state: GameState): boolean {
  return state.stats.totalPrestiges >= 10 && state.stats.totalGoldenBeansEarned >= 100;
}

/** Current stage from total beans */
export function computeCurrentStage(totalBeans: number): number {
  let stage = 1;
  for (const s of STAGES) {
    if (totalBeans >= s.requiredBeans) stage = s.id;
  }
  return stage;
}

/** Offline efficiency (%) */
export function getOfflineEfficiency(state: GameState): number {
  let eff = 30;
  if (state.prestigeUpgrades['G4']) eff += 20;
  if (state.prestigeUpgrades['G11']) eff = 100;
  eff += getAchievementBonusSum(state, 'offlinePct');
  return Math.min(eff, 100);
}

/** Offline earnings */
export function computeOfflineEarnings(state: GameState, elapsedSeconds: number): number {
  const cap = 8 * 3600;
  const actual = Math.min(elapsedSeconds, cap);
  const bps = computeBPS(state);
  const eff = getOfflineEfficiency(state) / 100;
  return Math.floor(bps * actual * eff);
}

/** Producer unlock check */
export function isProducerUnlocked(def: ProducerDef, state: GameState): boolean {
  return state.stats.totalBeans >= def.unlockBeans;
}

// ═══════════════════════════════════════════════
//  Internal Helpers
// ═══════════════════════════════════════════════

function getAchievementBonusSum(state: GameState, type: AchievementReward['type']): number {
  let sum = 0;
  for (const ach of ACHIEVEMENTS) {
    if (state.unlockedAchievements.includes(ach.id) && ach.reward.type === type) {
      sum += ach.reward.amount;
    }
  }
  return sum;
}

function getVarietyBonusSum(state: GameState, bonusType: string): number {
  let sum = 0;
  for (const v of VARIETIES) {
    if (state.unlockedVarieties.includes(v.id) && v.bonusType === bonusType) {
      sum += v.bonusPercent;
    }
  }
  return sum;
}

// ═══════════════════════════════════════════════
//  Number Formatting
// ═══════════════════════════════════════════════

const UNITS = [
  { value: 1e15, suffix: 'Qa' },
  { value: 1e12, suffix: 'T' },
  { value: 1e9,  suffix: 'B' },
  { value: 1e6,  suffix: 'M' },
  { value: 1e3,  suffix: 'K' },
];

export function formatNumber(n: number): string {
  if (n < 1000) return Math.floor(n).toString();
  for (const u of UNITS) {
    if (n >= u.value) {
      const v = n / u.value;
      return v >= 100 ? `${Math.floor(v)}${u.suffix}` : `${v.toFixed(1)}${u.suffix}`;
    }
  }
  return Math.floor(n).toString();
}

export function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}
